import {
    Component,
    Inject,
    OnDestroy,
    Renderer2,
    ChangeDetectionStrategy,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import { MeSupplyChainsResponse } from 'api/models';
import { Pipe, PipeTransform } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MaterialModule } from '@app/shared/material/material.module';
import { ModalDialogComponent } from '@app/shared/components/modal-dialog/modal-dialog.component';

export interface SelectedActors {
    consignor?: { key: string; name: string };
    carrier?: { key: string; name: string };
    consignee?: { key: string; name: string };
}

@Pipe({
    name: 'filterByName',
})
export class FilterByNamePipe implements PipeTransform {
    transform(items: any[], searchTerm: string): any[] {
        if (!items) {
            return [];
        }
        if (!searchTerm) {
            return items;
        }

        searchTerm = searchTerm.toLowerCase();

        return items.filter(
            item =>
                item.fullSiteName &&
                item.fullSiteName.toLowerCase().includes(searchTerm)
        );
    }
}

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-chain-selection-dialog',
    templateUrl: './chain-selection-dialog.component.html',
    styleUrls: ['./chain-selection-dialog.component.scss'],
    imports: [
        MaterialModule,
        TranslateModule,
        FormsModule,
        ModalDialogComponent,
        FilterByNamePipe,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChainSelectionDialogComponent implements OnDestroy {
    selectedActors: SelectedActors = {};
    consignorSelected = false;
    carrierSelected = false;
    consigneeSelected = false;

    consignorChain: any[] = [];
    carrierChain: any[] = [];
    consigneeChain: any[] = [];

    consignorSearchTerm: string = '';
    carrierSearchTerm: string = '';
    consigneeSearchTerm: string = '';

    consignorValue: string = '';
    carrierValue: string = '';
    consigneeValue: string = '';

    consignorKey: string = '';
    carrierKey: string = '';
    consigneeKey: string = '';

    constructor(
        private renderer: Renderer2,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            actors: MeSupplyChainsResponse;
        },
        private dialogRef: MatDialogRef<ChainSelectionDialogComponent>
    ) {
        this.renderer.addClass(document.body, 'disable-scroll-and-height');
        this.renderer.addClass(
            document.documentElement,
            'disable-scroll-and-height'
        );

        if (data?.actors) {
            this.consignorChain = data.actors.consignor || [];
            this.carrierChain = data.actors.carrier || [];
            this.consigneeChain = data.actors.consignee || [];
        }
    }

    trackByKey(index: number, item: any): string {
        return item?._key ?? index;
    }

    clearConsignorSearch(): void {
        this.consignorSearchTerm = '';
    }

    clearCarrierSearch(): void {
        this.carrierSearchTerm = '';
    }

    clearConsigneeSearch(): void {
        this.consigneeSearchTerm = '';
    }

    onConsignorClick(actor: any): void {
        if (actor) {
            this.consignorValue = actor.fullSiteName;

            this.selectedActors.consignor = {
                key: actor._key,
                name: actor.name,
            };
            this.consignorSelected = true;
            this.consignorKey = actor._key;
        }
    }

    onCarrierClick(actor: any): void {
        if (actor) {
            this.carrierValue = actor.fullSiteName;

            this.selectedActors.carrier = {
                key: actor._key,
                name: actor.name,
            };
            this.carrierSelected = true;
            this.carrierKey = actor._key;
        }
    }

    onConsigneeClick(actor: any): void {
        if (actor) {
            this.consigneeValue = actor.fullSiteName;

            this.selectedActors.consignee = {
                key: actor._key,
                name: actor.name,
            };
            this.consigneeSelected = true;
            this.consigneeKey = actor._key;
        }
    }

    getIconName(icon: string): string {
        switch (icon) {
            case 'direction':
                return 'arrow_forward';
            case 'truck':
                return 'local_shipping';
            case 'folder_checked':
                return 'folder';
            default:
                return 'category';
        }
    }

    isSelectionComplete(): boolean {
        return (
            this.consignorSelected &&
            this.carrierSelected &&
            this.consigneeSelected
        );
    }

    cancel(): void {
        this.cleanup();
        this.dialogRef.close(null);
    }

    confirm(): void {
        if (this.isSelectionComplete()) {
            this.cleanup();
            this.dialogRef.close(this.selectedActors);
        }
    }

    ngOnDestroy() {
        this.cleanup();
    }

    private cleanup(): void {
        this.renderer.removeClass(document.body, 'disable-scroll-and-height');
        this.renderer.removeClass(
            document.documentElement,
            'disable-scroll-and-height'
        );
    }
}
